export interface FilterLeaf {
  key: string;
  value: any;
  operation: string;
}

export interface FilterGroup {
  combinator: "AND" | "OR";
  filters: FilterItem[];
}

export type FilterItem = FilterLeaf | FilterGroup;

export interface SubviewConfig {
  id: string;
  label: string;
  display_order: number;
  filters: FilterItem[];
  columns?: string[];
}

// TODO: Replace this with the actual subview config from the configuration service
export const QUEUE_SUBVIEW_CONFIG: { subviewConfig: SubviewConfig[] } = {
  subviewConfig: [
    {
      id: "ready-for-distribution",
      label: "Ready for Distribution",
      display_order: 0,
      filters: [
        {
          combinator: "OR",
          filters: [
            {
              key: "workitem.hold_status",
              value: "OFF_HOLD",
              operation: "eq",
            },
            {
              key: "workitem.hold_status",
              value: null,
              operation: "is",
            },
          ],
        },
        { key: "workitem.owner", value: null, operation: "is" },
        {
          key: "workitem.case_status",
          value: ["SUBMITTED", "VOID", "CANCELLED", "SENT"],
          operation: "not in",
        },
        { key: "workitem.is_chargeable", value: true, operation: "eq" },
      ],
    },
    {
      id: "assigned",
      label: "Assigned",
      display_order: 1,
      filters: [
        {
          combinator: "OR",
          filters: [
            {
              key: "workitem.hold_status",
              value: "OFF_HOLD",
              operation: "eq",
            },
            {
              key: "workitem.hold_status",
              value: null,
              operation: "is",
            },
          ],
        },
        { key: "workitem.owner", value: null, operation: "is not" },
        {
          key: "workitem.case_status",
          value: ["SUBMITTED", "VOID", "CANCELLED", "SENT"],
          operation: "not in",
        },
        { key: "workitem.is_chargeable", value: true, operation: "eq" },
      ],
    },
    {
      id: "holds&requests",
      label: "Holds & Requests",
      display_order: 2,
      filters: [
        {
          key: "workitem.hold_status",
          value: "OFF_HOLD",
          operation: "ne",
        },
        {
          key: "workitem.hold_status",
          value: null,
          operation: "is not",
        },
        {
          key: "workitem.case_status",
          value: ["SUBMITTED", "VOID", "CANCELLED", "SENT"],
          operation: "not in",
        },
        { key: "workitem.is_chargeable", value: true, operation: "eq" },
      ],
    },
    {
      id: "submitted",
      label: "Submitted",
      display_order: 3,
      filters: [
        {
          key: "workitem.case_status",
          value: ["SUBMITTED", "VOID", "CANCELLED", "SENT"],
          operation: "in",
        },
        { key: "workitem.is_chargeable", value: true, operation: "eq" },
      ],
    },
    {
      id: "excluded",
      label: "Excluded",
      display_order: 4,
      filters: [
        { key: "workitem.is_chargeable", value: false, operation: "eq" },
      ],
    },
  ],
};

export const DEFAULT_SUBVIEW_ID = QUEUE_SUBVIEW_CONFIG.subviewConfig[0].id;

export const getSubviewById = (id: string | null): SubviewConfig | undefined =>
  QUEUE_SUBVIEW_CONFIG.subviewConfig.find((sv) => sv.id === id);

export const getSubviewFilters = (activeSubviewId: string | null) => {
  if (!activeSubviewId) return [];
  return getSubviewById(activeSubviewId)?.filters || [];
};

export const isQueueView = (view: any): boolean => view?.scope === "QUEUE_VIEW";

export const isNavViewSet = (scope?: string): boolean =>
  scope === "NAV_VIEW_SET";

// ── Nav View Sets ─────────────────────────────────────────────────────────────

export const NAV_SECTION_CONFIG_KEY = "view_nav_groupings_config";

export const LEGACY_MY_WORK_VIEW_NAMES = [
  "My Current",
  "My Recent",
  "My Holds",
  "Manager Review",
];
export const LEGACY_MY_TEAM_WORK_VIEW_NAMES = [
  "QA",
  "Current",
  "Recent",
  "On Hold",
];

export interface NavSubviewConfig {
  id: string;
  label: string;
  display_order: number;
  filters: FilterItem[];
  columns?: string[];
}

export interface NavViewSetConfig {
  id: string;
  label: string;
  section: "MY_WORK" | "MY_TEAM_WORK";
  subviews: NavSubviewConfig[];
}

export const MY_TEAMS_WORK_NAV_VIEW_SET_ID = "my-teams-work";

export const DEFAULT_NAV_VIEW_SET_CONFIG: {
  navViewConfig: NavViewSetConfig[];
} = {
  navViewConfig: [
    {
      id: "my-work",
      label: "My Work",
      section: "MY_WORK",
      subviews: [
        {
          id: "my-work-claimed",
          label: "Claimed",
          display_order: 0,
          filters: [
            { key: "workitem.owner", value: "MY_CASES", operation: "eq" },
            {
              key: "workitem.case_status",
              value: ["SUBMITTED", "VOID", "CANCELLED", "SENT"],
              operation: "not in",
            },
            {
              combinator: "OR",
              filters: [
                {
                  key: "workitem.hold_status",
                  value: "OFF_HOLD",
                  operation: "eq",
                },
                {
                  key: "workitem.hold_status",
                  value: null,
                  operation: "is",
                },
              ],
            },
          ],
          columns: [
            "aging_days",
            "codable_days",
            "case_status",
            "date_of_service",
            "patient_name",
            "__SL_IDENTIFIER__",
            "billing_provider",
            "facility",
            "billing_area",
            "subspecialty",
          ],
        },
        {
          id: "my-work-holds-requests",
          label: "Holds & Requests",
          display_order: 1,
          filters: [
            { key: "workitem.owner", value: "MY_CASES", operation: "eq" },
            { key: "workitem.hold_status", value: null, operation: "is not" },
            { key: "workitem.hold_status", value: "OFF_HOLD", operation: "ne" },
          ],
          columns: [
            "aging_days",
            "codable_days",
            "assignee",
            "hold_status",
            "date_of_service",
            "patient_name",
            "__SL_IDENTIFIER__",
            "billing_provider",
            "facility",
            "billing_area",
            "subspecialty",
          ],
        },
        {
          id: "my-work-submitted",
          label: "Submitted",
          display_order: 2,
          filters: [
            { key: "workitem.owner", value: "MY_CASES", operation: "eq" },
            {
              key: "workitem.case_status",
              value: ["SUBMITTED", "VOID", "CANCELLED", "SENT"],
              operation: "in",
            },
          ],
          columns: [
            "submitted_date",
            "case_status",
            "date_of_service",
            "patient_name",
            "__SL_IDENTIFIER__",
            "billing_provider",
            "facility",
            "billing_area",
            "subspecialty",
          ],
        },
      ],
    },
    {
      id: MY_TEAMS_WORK_NAV_VIEW_SET_ID,
      label: "My Team's Work",
      section: "MY_TEAM_WORK",
      subviews: [
        {
          id: "my-teams-work-feedback-required",
          label: "Feedback Required",
          display_order: 0,
          filters: [
            { key: "workitem.group_id", value: "MY_GROUPS", operation: "in" },
            {
              key: "workitem.hold_status",
              value: "MANAGER_REVIEW",
              operation: "eq",
            },
            {
              combinator: "OR",
              filters: [
                {
                  key: "workitem.owner",
                  value: "MY_CASES",
                  operation: "eq",
                },
                {
                  key: "workitem.owner",
                  value: null,
                  operation: "is",
                },
              ],
            },
          ],
          columns: [
            "aging_days",
            "codable_days",
            "profile",
            "owner",
            "assignee",
            "hold_status",
            "date_of_service",
            "patient_name",
            "__SL_IDENTIFIER__",
            "billing_provider",
            "facility",
            "billing_area",
            "subspecialty",
          ],
        },
        {
          id: "my-teams-work-ready-for-distribution",
          label: "Ready for Distribution",
          display_order: 1,
          filters: [
            { key: "workitem.group_id", value: "MY_GROUPS", operation: "in" },
            { key: "workitem.owner", value: null, operation: "is" },
            {
              key: "workitem.case_status",
              value: ["SUBMITTED", "VOID", "CANCELLED", "SENT"],
              operation: "not in",
            },
            { key: "workitem.is_chargeable", value: true, operation: "eq" },
            {
              combinator: "OR",
              filters: [
                {
                  key: "workitem.hold_status",
                  value: "OFF_HOLD",
                  operation: "eq",
                },
                {
                  key: "workitem.hold_status",
                  value: null,
                  operation: "is",
                },
              ],
            },
          ],
          columns: [
            "aging_days",
            "codable_days",
            "profile",
            "case_status",
            "date_of_service",
            "patient_name",
            "__SL_IDENTIFIER__",
            "billing_provider",
            "facility",
            "billing_area",
            "subspecialty",
          ],
        },
        {
          id: "my-teams-work-claimed",
          label: "Claimed",
          display_order: 2,
          filters: [
            { key: "workitem.group_id", value: "MY_GROUPS", operation: "in" },
            { key: "workitem.owner", value: null, operation: "is not" },
            {
              key: "workitem.case_status",
              value: ["SUBMITTED", "VOID", "CANCELLED", "SENT"],
              operation: "not in",
            },
            { key: "workitem.is_chargeable", value: true, operation: "eq" },
            {
              combinator: "OR",
              filters: [
                {
                  key: "workitem.hold_status",
                  value: "OFF_HOLD",
                  operation: "eq",
                },
                {
                  key: "workitem.hold_status",
                  value: null,
                  operation: "is",
                },
              ],
            },
          ],
          columns: [
            "aging_days",
            "codable_days",
            "profile",
            "case_status",
            "owner",
            "date_of_service",
            "patient_name",
            "__SL_IDENTIFIER__",
            "billing_provider",
            "facility",
            "billing_area",
            "subspecialty",
          ],
        },
        {
          id: "my-teams-work-submitted",
          label: "Submitted",
          display_order: 3,
          filters: [
            { key: "workitem.group_id", value: "MY_GROUPS", operation: "in" },
            {
              key: "workitem.case_status",
              value: ["SUBMITTED", "VOID", "CANCELLED", "SENT"],
              operation: "in",
            },
            { key: "workitem.is_chargeable", value: true, operation: "eq" },
          ],
          columns: [
            "submitted_date",
            "profile",
            "owner",
            "case_status",
            "date_of_service",
            "patient_name",
            "__SL_IDENTIFIER__",
            "billing_provider",
            "facility",
            "billing_area",
            "subspecialty",
          ],
        },
        {
          id: "my-teams-work-excluded",
          label: "Excluded",
          display_order: 4,
          filters: [
            { key: "workitem.group_id", value: "MY_GROUPS", operation: "in" },
            { key: "workitem.is_chargeable", value: false, operation: "eq" },
          ],
          columns: [
            "profile",
            "owner",
            "case_status",
            "date_of_service",
            "patient_name",
            "__SL_IDENTIFIER__",
            "billing_provider",
            "facility",
            "billing_area",
            "subspecialty",
          ],
        },
      ],
    },
  ],
};

const SL_IDENTIFIER_FIELD: Record<string, string> = {
  Pathology: "accession",
  Radiology: "accession",
  Surgery: "surgery_id",
  "Bedside Pro": "encounter",
};

/**
 * Resolves `__SL_IDENTIFIER__` placeholders in subview column arrays to the
 * correct service-line-specific field name. Call this once after fetching
 * (or falling back to) the nav view set config, passing the active service
 * line display name (e.g. "Pathology", "Surgery", "Bedside Pro").
 *
 * When the config service eventually returns service-line-specific configs
 * with no placeholder, this function is a no-op and can be removed.
 */
export const resolveNavViewSetConfig = (
  config: NavViewSetConfig[],
  serviceLine: string,
): NavViewSetConfig[] => {
  const identifier = SL_IDENTIFIER_FIELD[serviceLine] ?? null;
  return config.map((viewSet) => ({
    ...viewSet,
    subviews: [...viewSet.subviews].sort((a, b) => a.display_order - b.display_order).map((sv) => ({
      ...sv,
      columns: sv.columns
        ? (sv.columns
            .map((f) => (f === "__SL_IDENTIFIER__" ? identifier : f))
            .filter(Boolean) as string[])
        : sv.columns,
    })),
  }));
};

// With the left nav enabled the sidebar can select either a real view or a nav view set +
// subview, so restoring the selection has to record which of the two it was. The legacy
// lastView key (see index.tsx) holds only a view_id and stays as-is for the tab layout.
const NAV_SELECTION_STORAGE_PREFIX = "worklist:lastNavSelection";

export type PersistedNavSelection =
  | { kind: "view"; viewId: string }
  | { kind: "navViewSet"; navViewSetId: string; subviewId: string };

export const buildNavSelectionStorageKey = (
  widgetType: string,
  serviceLineName: string | undefined,
  tenantId: string | undefined
): string =>
  `${NAV_SELECTION_STORAGE_PREFIX}:${widgetType ?? ""}:${serviceLineName ?? ""}:${tenantId ?? ""}`;

export const readPersistedNavSelection = (key: string): PersistedNavSelection | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.kind === "view" && typeof parsed.viewId === "string") {
      return { kind: "view", viewId: parsed.viewId };
    }
    if (
      parsed?.kind === "navViewSet" &&
      typeof parsed.navViewSetId === "string" &&
      typeof parsed.subviewId === "string"
    ) {
      return {
        kind: "navViewSet",
        navViewSetId: parsed.navViewSetId,
        subviewId: parsed.subviewId,
      };
    }
    return null;
  } catch {
    return null;
  }
};

export const persistNavSelection = (key: string, selection: PersistedNavSelection): void => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(selection));
  } catch {
    // sessionStorage may be unavailable (private mode / quota); persistence is best-effort.
  }
};

export const OWNER_TRANSFER_VIEW_NAMES = Object.freeze([
  "On Hold",
  "Current",
  "My Current",
  "My Holds",
]);

export const OWNER_TRANSFER_NAV_SUBVIEW_IDS = Object.freeze([
  "my-work-claimed",
  "my-work-holds-requests",
  "my-teams-work-claimed",
  "my-teams-work-ready-for-distribution",
]);

export const coderRedistributionViews = Object.freeze([
  "My Current",
  "My Holds",
]);

export const CODER_TRANSFER_NAV_SUBVIEW_IDS = Object.freeze([
  "my-work-claimed",
  "my-work-holds-requests",
]);

export const isOwnerTransferView = (
  view: { scope?: string; name?: string } | null | undefined,
  navSubviewId: string | null | undefined,
): boolean =>
  isNavViewSet(view?.scope)
    ? navSubviewId != null && OWNER_TRANSFER_NAV_SUBVIEW_IDS.includes(navSubviewId)
    : OWNER_TRANSFER_VIEW_NAMES.includes(view?.name ?? "");

export const resolveValidNavSubviewId = (
  config: NavViewSetConfig[],
  navViewSetId: string | null,
  subviewId: string | null | undefined,
): string | null => {
  if (!navViewSetId || !subviewId) return null;
  const set = config.find((vs) => vs.id === navViewSetId);
  return set?.subviews.some((sv) => sv.id === subviewId) ? subviewId : null;
};
